import { DesignSystemVersion, Kit, ChatRef } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '../constants';
import {
	updateActiveDesignSystemVersion,
	updateDsChatRefs,
	updateLatestPublishedVersionNumber,
	setDesignSystemLoadError,
} from '../redux/db/actions';
import { setDesignSystemContent } from '../redux/mode/actions';
import { createDesignSystemContent } from '../designSystem/content/factory';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class DesignSystemVersionPollingStream extends PollingStream<DesignSystemVersion | null> {
	private lastKit: Kit | null = null;
	private lastChatRefs: ChatRef[] = [];
	private lastLatestPublishedVersionNumber: number | null = null;

	constructor(designSystemId: string) {
		const url = VITE_FORGE_URL + `/poll/design-system-version/${designSystemId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setDesignSystemLoadError('You do not have access to this design system'));
				store.dispatch(updateActiveDesignSystemVersion(null));
				store.dispatch(updateLatestPublishedVersionNumber(null));
				store.dispatch(setDesignSystemContent(null));
				store.dispatch(updateDsChatRefs([]));
			} else {
				console.error('DesignSystemVersionPollingStream error:', error);
			}
		};

		super(url, onError, 'activeDesignSystemVersion');
	}

	extractData(response: any): DesignSystemVersion[] {
		// DesignSystemVersion endpoint returns a single DesignSystemVersion object or null
		if (response?.version === null || response?.version === undefined) {
			this.lastKit = null;
			this.lastChatRefs = [];
			this.lastLatestPublishedVersionNumber = null;
			return [];
		}

		// Store kit, chatRefs, and latestPublishedVersionNumber for processing in onData
		this.lastKit = response.kit || null;
		this.lastChatRefs = response.chatRefs || [];
		this.lastLatestPublishedVersionNumber = response.latestPublishedVersionNumber ?? null;

		return [response.version];
	}

	onData(versions: DesignSystemVersion[]) {
		const version = versions.length > 0 ? versions[0] : null;
		if (!version) {
			store.dispatch(updateActiveDesignSystemVersion(null));
			store.dispatch(updateLatestPublishedVersionNumber(null));
			store.dispatch(setDesignSystemContent(null));
			store.dispatch(updateDsChatRefs([]));
			return;
		}

		// The backend already returns DesignSystemVersion objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedVersion: DesignSystemVersion = {
			...version,
			createdAt: new Date(version.createdAt),
		};

		// since DS versions don't have optimistic writes (they aren't changed on the FE currently) we don't need to resolve them
		store.dispatch(updateActiveDesignSystemVersion(processedVersion));

		// Process chat refs - convert date strings to Date objects
		const processedChatRefs: ChatRef[] = this.lastChatRefs.map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateDsChatRefs(processedChatRefs));

		// Update latest published version number
		store.dispatch(updateLatestPublishedVersionNumber(this.lastLatestPublishedVersionNumber));

		// Process kit if available
		if (this.lastKit) {
			const content = createDesignSystemContent(processedVersion.id, this.lastKit);
			store.dispatch(setDesignSystemContent(content));
		} else {
			// If no kit, set content to null
			store.dispatch(setDesignSystemContent(null));
		}
	}
}
